import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { COUNTRY_CITY, countries, cityToCountry } from "./locationOptions";
import { presignUpload, uploadFileToS3 } from "./venuesApi";

import FormModal from "../../../components/form/FormModal";
import FormButton from "../../../components/form/FormButton";
import FormCheckbox from "../../../components/form/FormCheckbox";
import FormField from "../../../components/form/FormField";
import FormInput from "../../../components/form/FormInput";
import FormSelect from "../../../components/form/FormSelect";
import FormTextarea from "../../../components/form/FormTextarea";

import "./venue.css";

const PHONE_DIGIT_RE = /^\d{6,15}$/;
const onlyDigits = (s) => String(s || "").replace(/\D+/g, "");

const FACILITIES = [
  "parking",
  "washroom",
  "changing_room",
  "lights",
  "cafeteria",
  "equipment_rental",
];

const TAGS = ["featured", "premium", "popular"];

const emptyForm = {
  name: "",
  shortDescription: "",
  fullDescription: "",
  location: {
    address: "",
    city: "",
    country: "",
    latitude: "",
    longitude: "",
  },
  media: {
    bannerImage: "",
    bannerKey: "",
    gallery: [],
    carouselKeys: [],
  },
  facilities: [],
  tags: [],
  featuredPriority: 0,
  policies: {
    cancellationPolicy: "",
    rulesTerms: "",
    openTime: "06:00",
    closeTime: "22:00",
  },
  googleMapsUrl: "",
  pricing: {
    basePrice: "",
  },
  contactNumber: "",
  active: true,
};

const MAX_BANNER = 8 * 1024 * 1024;
const MAX_GALLERY = 8 * 1024 * 1024;

const ALLOWED_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/gif",
  "image/avif",
  "image/svg+xml",
];

const getFirstErrorMessage = (errorsObj) => {
  const firstKey = Object.keys(errorsObj || {})[0];
  return firstKey ? errorsObj[firstKey] : "";
};

const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const VenueForm = ({
  open,
  mode = "create",
  initialData = null,
  onCancel,
  onSubmit,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  const [bannerFile, setBannerFile] = useState(null);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [bannerProgress, setBannerProgress] = useState(0);

  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [galleryProgress, setGalleryProgress] = useState(0);

  const [bannerDirty, setBannerDirty] = useState(false);
  const [galleryDirty, setGalleryDirty] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  const adminCtx = useMemo(() => {
    if (typeof window === "undefined") return null;

    try {
      const raw = window.localStorage.getItem("va_ctx");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const isCountryAdmin =
    adminCtx?.role === "country_admin" && !!adminCtx?.country;

  const isVenueAdmin = adminCtx?.role === "venue_admin";
  const disableTags = isVenueAdmin && mode === "edit";

  const countryOptions = useMemo(() => {
    if (isCountryAdmin) {
      const match = countries.find((country) => country.code === adminCtx.country);
      return match ? [match] : countries;
    }

    return countries;
  }, [adminCtx, isCountryAdmin]);

  useEffect(() => {
    setErrors({});
    setBannerFile(null);
    setGalleryFiles([]);
    setBannerProgress(0);
    setGalleryProgress(0);
    setBannerDirty(false);
    setGalleryDirty(false);

    if (!initialData) {
      setForm(emptyForm);
      return;
    }

    const nextForm = {
      ...emptyForm,
      ...initialData,
      location: {
        ...emptyForm.location,
        ...(initialData.location || {}),
      },
      media: {
        ...emptyForm.media,
        ...(initialData.media || {}),
        gallery: Array.isArray(initialData.media?.gallery)
          ? initialData.media.gallery
          : [],
        carouselKeys: Array.isArray(initialData.media?.carouselKeys)
          ? initialData.media.carouselKeys
          : [],
      },
      facilities: Array.isArray(initialData.facilities)
        ? initialData.facilities
        : [],
      tags: Array.isArray(initialData.tags) ? initialData.tags : [],
      featuredPriority:
        Number.isFinite(Number(initialData.featuredPriority)) &&
        Number(initialData.featuredPriority) > 0
          ? Math.floor(Number(initialData.featuredPriority))
          : 0,
      policies: {
        ...emptyForm.policies,
        ...(initialData.policies || {}),
      },
      pricing: {
        ...emptyForm.pricing,
        ...(initialData.pricing || {}),
      },
    };

    if (!nextForm.contactNumber) {
      const nationalNumber = initialData?.contact?.nationalNumber || "";
      const e164 = initialData?.contact?.e164 || "";
      nextForm.contactNumber =
        nationalNumber || onlyDigits(String(e164 || "").replace(/^\+/, ""));
    }

    setForm(nextForm);
  }, [initialData]);

  useEffect(() => {
    if (!initialData && isCountryAdmin && adminCtx?.country) {
      setForm((prev) => ({
        ...prev,
        location: {
          ...(prev.location || {}),
          country: adminCtx.country,
        },
      }));
    }
  }, [initialData, isCountryAdmin, adminCtx]);

  const selectedCountry = form.location?.country || "";
  const cityOptions = COUNTRY_CITY[selectedCountry]?.cities || [];

  const set = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setLocation = (patch) => {
    setForm((prev) => ({
      ...prev,
      location: {
        ...(prev.location || {}),
        ...patch,
      },
    }));
  };

  const setPolicy = (key, value) => {
    setForm((prev) => ({
      ...prev,
      policies: {
        ...(prev.policies || {}),
        [key]: value,
      },
    }));
  };

  const setPricing = (key, value) => {
    setForm((prev) => ({
      ...prev,
      pricing: {
        ...(prev.pricing || {}),
        [key]: value,
      },
    }));
  };

  const clearError = (key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;

      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const toggleInArray = (field, value) => {
    setForm((prev) => {
      const nextSet = new Set(prev[field] || []);

      if (nextSet.has(value)) {
        nextSet.delete(value);
      } else {
        nextSet.add(value);
      }

      return {
        ...prev,
        [field]: Array.from(nextSet),
      };
    });
  };

  const slugify = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[@&%*^~`'"!?,.<>()[\]{}|\\/+]+/g, " ")
      .replace(/[^a-z0-9\s-]/g, " ")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");

  const extFromContentType = (contentType) => {
    const type = String(contentType || "").toLowerCase();

    if (type === "image/png") return "png";
    if (type === "image/webp") return "webp";
    if (type === "image/avif") return "avif";
    if (type === "image/svg+xml") return "svg";
    if (type === "image/gif") return "gif";

    return "jpg";
  };

  const venueSlug = () => slugify(form.name || "venue");
  const citySlug = () => slugify(form.location?.city || "unknown-city");
  const venueImagesFolder = () => `venues/${citySlug()}/${venueSlug()}/images`;

  const joinUrl = (base, path) => {
    const safeBase = String(base || "").replace(/\/+$/g, "");
    const safePath = String(path || "").replace(/^\/+/g, "");

    return safeBase && safePath ? `${safeBase}/${safePath}` : "";
  };

  const buildPreviewUrlFromKey = (key) => {
    if (!key) return "";

    const assetBase = process.env.REACT_APP_ASSET_BASE_URL;

    if (!assetBase) {
      console.error("[PREVIEW] Missing REACT_APP_ASSET_BASE_URL");
      toast.error("Asset preview URL is not configured.");
      return "";
    }

    const url = joinUrl(assetBase, key);
    console.log("[PREVIEW] resolved", { key, url });

    return url;
  };

  const setBanner = ({ filePath }) => {
    setBannerDirty(true);

    setForm((prev) => ({
      ...prev,
      media: {
        ...(prev.media || {}),
        bannerKey: filePath,
        bannerImage: buildPreviewUrlFromKey(filePath),
      },
    }));
  };

  const pushGallery = ({ filePath }) => {
    setGalleryDirty(true);

    setForm((prev) => {
      const carouselKeys = Array.isArray(prev.media?.carouselKeys)
        ? [...prev.media.carouselKeys]
        : [];

      carouselKeys.push(filePath);

      return {
        ...prev,
        media: {
          ...(prev.media || {}),
          carouselKeys,
          gallery: carouselKeys.map(buildPreviewUrlFromKey),
        },
      };
    });
  };

  const removeGalleryAt = (index) => {
    setGalleryDirty(true);

    setForm((prev) => {
      const carouselKeys = Array.isArray(prev.media?.carouselKeys)
        ? [...prev.media.carouselKeys]
        : [];

      if (index >= 0 && index < carouselKeys.length) {
        carouselKeys.splice(index, 1);
      }

      return {
        ...prev,
        media: {
          ...(prev.media || {}),
          carouselKeys,
          gallery: carouselKeys.map(buildPreviewUrlFromKey),
        },
      };
    });

    toast.info("Gallery image removed.");
  };

  const onBannerPick = (file) => {
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      const message = "Unsupported banner image type.";
      setErrors((prev) => ({ ...prev, banner: message }));
      toast.error(message);
      return;
    }

    if (file.size > MAX_BANNER) {
      const message = "Banner image is too large. Maximum allowed size is 8MB.";
      setErrors((prev) => ({ ...prev, banner: message }));
      toast.error(message);
      return;
    }

    setBannerFile(file);
    clearError("banner");
    clearError("media");
    toast.info("Banner selected. Click Upload to save it.");
  };

  const uploadBanner = async () => {
    if (!bannerFile || bannerUploading) return;

    if (!form.name?.trim() || !form.location?.city?.trim()) {
      const message = "Please enter Venue Name and City before uploading banner.";
      setErrors((prev) => ({
        ...prev,
        banner: message,
      }));
      toast.error(message);
      return;
    }

    setBannerUploading(true);
    setBannerProgress(0);

    try {
      const folder = venueImagesFolder();
      const ext = extFromContentType(bannerFile.type);
      const filename = `banner_${Date.now()}.${ext}`;

      console.log("[BANNER] presign payload", {
        folder,
        filename,
        contentType: bannerFile.type,
      });

      const presign = await presignUpload({
        folder,
        filename,
        contentType: bannerFile.type,
      });

      console.log("[BANNER] presign response", presign);
      console.log("[BANNER] uploading to", presign?.uploadUrl);

      const uploaded = await uploadFileToS3({
        presign,
        file: bannerFile,
        onProgress: (nextProgress) => setBannerProgress(nextProgress),
      });

      setBanner({ filePath: uploaded.filePath });
      setBannerFile(null);
      setBannerProgress(100);
      clearError("banner");
      clearError("media");
      toast.success("Banner uploaded successfully.");
    } catch (error) {
      console.error("[BANNER] upload failed:", error);
      console.error("[BANNER] err.message:", error?.message);
      console.error("[BANNER] err.response?.status:", error?.response?.status);
      console.error("[BANNER] err.response?.data:", error?.response?.data);

      const message = getApiErrorMessage(error, "Banner upload failed.");
      setErrors((prev) => ({ ...prev, banner: message }));
      toast.error(message);
    } finally {
      setBannerUploading(false);
      setTimeout(() => setBannerProgress(0), 600);
    }
  };

  const onGalleryPick = (files) => {
    const selectedFiles = Array.from(files || []);
    const validFiles = [];

    for (const file of selectedFiles) {
      if (!ALLOWED_TYPES.includes(file.type)) continue;
      if (file.size > MAX_GALLERY) continue;

      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      const message = "No valid gallery images selected. Check image type and size.";
      setErrors((prev) => ({
        ...prev,
        gallery: message,
      }));
      toast.error(message);
      return;
    }

    setGalleryFiles(validFiles);
    clearError("gallery");
    clearError("media");
    toast.info(`${validFiles.length} gallery image(s) selected. Click Upload Selected.`);
  };

  const uploadGallery = async () => {
    if (!galleryFiles.length || galleryUploading) return;

    if (!form.name?.trim() || !form.location?.city?.trim()) {
      const message = "Please enter Venue Name and City before uploading gallery.";
      setErrors((prev) => ({
        ...prev,
        gallery: message,
      }));
      toast.error(message);
      return;
    }

    setGalleryUploading(true);
    setGalleryProgress(0);

    try {
      const folder = venueImagesFolder();

      for (let index = 0; index < galleryFiles.length; index += 1) {
        const file = galleryFiles[index];
        const ext = extFromContentType(file.type);
        const filename = `image_${index + 1}_${Date.now()}.${ext}`;

        const presign = await presignUpload({
          folder,
          filename,
          contentType: file.type,
        });

        const uploaded = await uploadFileToS3({
          presign,
          file,
          onProgress: (nextProgress) => {
            const base = Math.floor((index / galleryFiles.length) * 100);
            const scaled = Math.floor(nextProgress / galleryFiles.length);
            setGalleryProgress(Math.min(99, base + scaled));
          },
        });

        pushGallery({ filePath: uploaded.filePath });
      }

      setGalleryFiles([]);
      setGalleryProgress(100);
      clearError("gallery");
      clearError("media");
      toast.success("Gallery uploaded successfully.");
    } catch (error) {
      console.error(error);
      const message = getApiErrorMessage(error, "Gallery upload failed.");
      setErrors((prev) => ({ ...prev, gallery: message }));
      toast.error(message);
    } finally {
      setGalleryUploading(false);
      setTimeout(() => setGalleryProgress(0), 600);
    }
  };

  const validate = () => {
    const nextErrors = {};

    if (!String(form.name || "").trim()) {
      nextErrors.name = "Name is required";
    }

    if (!String(form.location?.city || "").trim()) {
      nextErrors.city = "City is required";
    }

    if (!String(form.location?.country || "").trim()) {
      nextErrors.country = "Country is required";
    }

    const latRaw = String(form.location?.latitude ?? "").trim();
    const lngRaw = String(form.location?.longitude ?? "").trim();

    if (!latRaw) {
      nextErrors.latitude = "Latitude is required";
    } else if (Number.isNaN(parseFloat(latRaw))) {
      nextErrors.latitude = "Latitude must be a number";
    }

    if (!lngRaw) {
      nextErrors.longitude = "Longitude is required";
    } else if (Number.isNaN(parseFloat(lngRaw))) {
      nextErrors.longitude = "Longitude must be a number";
    }

    const basePrice = parseFloat(form.pricing?.basePrice);

    if (form.pricing?.basePrice !== "" && Number.isNaN(basePrice)) {
      nextErrors.basePrice = "Base price must be a number";
    }

    const featuredPriority = Number(form.featuredPriority ?? 0);
    if (
      !Number.isFinite(featuredPriority) ||
      featuredPriority < 0 ||
      !Number.isInteger(featuredPriority)
    ) {
      nextErrors.featuredPriority =
        "Featured priority must be a whole number of 0 or higher";
    }

    if (form.contactNumber) {
      const digits = onlyDigits(form.contactNumber);

      if (!PHONE_DIGIT_RE.test(digits)) {
        nextErrors.contactNumber = "Enter 6–15 digits only for phone number";
      }
    }

    setErrors(nextErrors);
    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (bannerUploading || galleryUploading || submitting) return;

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      toast.error(getFirstErrorMessage(validationErrors) || "Please fix validation errors.");
      return;
    }

    if (bannerFile || galleryFiles.length) {
      const message = "You selected images but did not upload them. Click Upload first.";
      setErrors((prev) => ({
        ...prev,
        media: message,
      }));
      toast.warning(message);
      return;
    }

    const latitude =
      form.location.latitude === "" ? undefined : parseFloat(form.location.latitude);

    const longitude =
      form.location.longitude === "" ? undefined : parseFloat(form.location.longitude);

    const basePrice =
      form.pricing.basePrice === "" ? undefined : parseFloat(form.pricing.basePrice);

    console.log("[SUBMIT] media snapshot", {
      bannerKey: form.media?.bannerKey,
      carouselKeys: form.media?.carouselKeys,
      bannerImage: form.media?.bannerImage,
      galleryCount: (form.media?.gallery || []).length,
    });

    const payload = {
      name: form.name.trim(),
      shortDescription: String(form.shortDescription || "").trim(),
      fullDescription: String(form.fullDescription || "").trim(),

      location: {
        address: String(form.location?.address || "").trim(),
        city: String(form.location?.city || "").trim(),
        country: String(form.location?.country || "").trim(),
        latitude,
        longitude,
      },

      googleMapsUrl: String(form.googleMapsUrl || "").trim(),

      media:
        mode === "edit"
          ? {
              ...(bannerDirty ? { bannerKey: form.media?.bannerKey || "" } : {}),
              ...(galleryDirty
                ? {
                    carouselKeys: Array.isArray(form.media?.carouselKeys)
                      ? form.media.carouselKeys
                      : [],
                    ...(Array.isArray(form.media?.carouselKeys) &&
                    form.media.carouselKeys.length === 0
                      ? { clearCarousel: true }
                      : {}),
                  }
                : {}),
            }
          : {
              bannerKey: form.media?.bannerKey || "",
              carouselKeys: Array.isArray(form.media?.carouselKeys)
                ? form.media.carouselKeys
                : [],
            },

      facilities: Array.isArray(form.facilities) ? form.facilities : [],
      tags: Array.isArray(form.tags) ? form.tags : [],
      featuredPriority: Math.max(
        0,
        Math.floor(Number(form.featuredPriority) || 0),
      ),

      policies: {
        cancellationPolicy: String(form.policies?.cancellationPolicy || "").trim(),
        rulesTerms: String(form.policies?.rulesTerms || "").trim(),
        openTime: form.policies?.openTime || "06:00",
        closeTime: form.policies?.closeTime || "22:00",
      },

      pricing: {
        basePrice,
      },

      active: !!form.active,

      ...(form.contactNumber ? { contactNumber: onlyDigits(form.contactNumber) } : {}),
    };

    console.log(
      mode === "edit" ? "[FE_UPDATE_VENUE] payload.media" : "[FE_CREATE_VENUE] payload.media",
      payload.media
    );

    try {
      setSubmitting(true);
      await onSubmit?.(payload);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Unable to save venue. Please try again."));
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (bannerUploading || galleryUploading || submitting) return;
    onCancel?.();
  };

  return (
    <FormModal
      open={open}
      title={mode === "create" ? "Create Venue" : "Edit Venue"}
      width="880px"
      submitting={bannerUploading || galleryUploading || submitting}
      submitText={mode === "create" ? "Create Venue" : "Save Changes"}
      submittingText={
        bannerUploading
          ? `Uploading ${bannerProgress || 0}%`
          : galleryUploading
          ? `Uploading ${galleryProgress || 0}%`
          : "Saving..."
      }
      cancelText="Cancel"
      onClose={handleClose}
      onSubmit={handleSubmit}
    >
      <div className="venue-form">
        <div className="venue-grid">
          <FormField label="Name *" error={errors.name} className="venue-span-2">
            <FormInput
              type="text"
              value={form.name}
              onChange={(event) => {
                set("name", event.target.value);
                clearError("name");
              }}
              placeholder="Greenfield Sports Arena"
              required
            />
          </FormField>

          <FormField label="Short Description">
            <FormInput
              type="text"
              value={form.shortDescription}
              onChange={(event) => set("shortDescription", event.target.value)}
              placeholder="Premium indoor facility"
            />
          </FormField>

          <FormField label="Full Description" className="venue-span-2">
            <FormTextarea
              rows={4}
              value={form.fullDescription}
              onChange={(event) => set("fullDescription", event.target.value)}
              placeholder="Describe the venue, surfaces, amenities, etc."
            />
          </FormField>

          <FormField label="Address">
            <FormInput
              type="text"
              value={form.location?.address || ""}
              onChange={(event) => setLocation({ address: event.target.value })}
              placeholder="Street / Area"
            />
          </FormField>

          <FormField label="Country *" error={errors.country}>
            <FormSelect
              value={selectedCountry}
              onChange={(event) => {
                setLocation({ country: event.target.value, city: "" });
                clearError("country");
                clearError("city");
              }}
              required
              disabled={isCountryAdmin}
            >
              <option value="">Choose country…</option>
              {countryOptions.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name} ({country.code})
                </option>
              ))}
            </FormSelect>
          </FormField>

          <FormField label="City *" error={errors.city}>
            <FormSelect
              value={form.location?.city || ""}
              onChange={(event) => {
                setLocation({
                  city: event.target.value,
                  country: cityToCountry[event.target.value] || selectedCountry,
                });
                clearError("city");
              }}
              disabled={!selectedCountry}
              required
            >
              <option value="">
                {selectedCountry ? "Choose city…" : "Select country first"}
              </option>
              {cityOptions.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </FormSelect>
          </FormField>

          <FormField label="Phone (optional)" error={errors.contactNumber}>
            <FormInput
              type="text"
              inputMode="numeric"
              pattern="\d*"
              maxLength={15}
              value={form.contactNumber}
              onChange={(event) => {
                set("contactNumber", onlyDigits(event.target.value));
                clearError("contactNumber");
              }}
              placeholder="e.g. 12345678"
              title="Digits only (6–15). Country code is inferred from the selected Country."
            />
          </FormField>

          <FormField label="Latitude *" error={errors.latitude}>
            <FormInput
              type="text"
              value={form.location?.latitude ?? ""}
              onChange={(event) => {
                setLocation({ latitude: event.target.value });
                clearError("latitude");
              }}
              placeholder="19.076"
            />
          </FormField>

          <FormField label="Longitude *" error={errors.longitude}>
            <FormInput
              type="text"
              value={form.location?.longitude ?? ""}
              onChange={(event) => {
                setLocation({ longitude: event.target.value });
                clearError("longitude");
              }}
              placeholder="72.8777"
            />
          </FormField>

          <FormField
            label="Google Maps URL"
            hint="Paste the exact Google Maps link (Share → Copy link)."
            className="venue-span-2"
          >
            <FormInput
              type="url"
              value={form.googleMapsUrl || ""}
              onChange={(event) => set("googleMapsUrl", event.target.value)}
              placeholder="https://maps.app.goo.gl/YosJmxyXKuhtAeYt8"
            />
          </FormField>

          <FormField label="Banner Image" error={errors.banner} className="venue-span-2">
            <div className="venue-upload-row">
              <FormInput
                type="file"
                accept="image/*"
                onChange={(event) => onBannerPick(event.target.files?.[0])}
                disabled={bannerUploading || submitting}
              />

              <FormButton
                type="button"
                variant="primary"
                onClick={uploadBanner}
                disabled={!bannerFile || bannerUploading || submitting}
                className="venue-upload-btn"
                title="Upload banner to S3"
              >
                {bannerUploading ? `Uploading ${bannerProgress || 0}%` : "Upload"}
              </FormButton>
            </div>

            <FormInput
              type="text"
              value={form.media?.bannerImage || ""}
              placeholder="S3 URL will appear here after upload"
              readOnly
              className="venue-media-url"
            />

            <input type="hidden" value={form.media?.bannerKey || ""} readOnly />

            <div className="venue-banner-preview">
              {form.media?.bannerImage ? (
                <img src={form.media.bannerImage} alt="Banner" />
              ) : (
                <div className="venue-image-placeholder">Upload a banner image</div>
              )}
            </div>
          </FormField>

          <FormField label="Gallery Images" error={errors.gallery || errors.media} className="venue-span-2">
            <div className="venue-upload-row">
              <FormInput
                type="file"
                accept="image/*"
                multiple
                onChange={(event) => onGalleryPick(event.target.files)}
                disabled={galleryUploading || submitting}
              />

              <FormButton
                type="button"
                variant="primary"
                onClick={uploadGallery}
                disabled={!galleryFiles.length || galleryUploading || submitting}
                className="venue-upload-btn"
                title="Upload selected images to S3"
              >
                {galleryUploading
                  ? `Uploading ${galleryProgress || 0}%`
                  : "Upload Selected"}
              </FormButton>
            </div>

            <div className="venue-gallery-grid">
              {(form.media?.gallery || []).map((url, index) => (
                <div key={`${url}-${index}`} className="venue-gallery-thumb">
                  <img src={url} alt={`Gallery ${index + 1}`} />

                  <button
                    type="button"
                    className="venue-gallery-remove"
                    onClick={() => removeGalleryAt(index)}
                    title="Remove image"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </FormField>

          <FormField label="Facilities" className="venue-span-2">
            <div className="venue-tile-grid" role="group" aria-label="Facilities">
              {FACILITIES.map((facility) => {
                const checked = form.facilities.includes(facility);
                const label = facility.replace(/_/g, " ");

                return (
                  <button
                    key={facility}
                    type="button"
                    className={`venue-tile ${checked ? "is-selected" : ""}`}
                    onClick={() => toggleInArray("facilities", facility)}
                    aria-pressed={checked}
                    title={label}
                  >
                    <input
                      className="venue-tile-input"
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleInArray("facilities", facility)}
                      tabIndex={-1}
                      aria-hidden="true"
                    />

                    <span className="venue-tile-label">{label}</span>
                    <span className="venue-tile-sub">
                      Tap to {checked ? "remove" : "add"}
                    </span>
                  </button>
                );
              })}
            </div>
          </FormField>

          <FormField
            label="Tags"
            hint={disableTags ? "Tags are locked for Venue Admins in edit mode." : ""}
            className="venue-span-2"
          >
            <div
              className={`venue-tile-grid venue-tile-grid-tags ${
                disableTags ? "is-disabled" : ""
              }`}
              role="group"
              aria-label="Tags"
            >
              {TAGS.map((tag) => {
                const checked = form.tags.includes(tag);

                return (
                  <button
                    key={tag}
                    type="button"
                    className={`venue-tile venue-tile-tag ${
                      checked ? "is-selected" : ""
                    }`}
                    onClick={() => toggleInArray("tags", tag)}
                    aria-pressed={checked}
                    disabled={disableTags}
                    title={tag}
                  >
                    <input
                      className="venue-tile-input"
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleInArray("tags", tag)}
                      disabled={disableTags}
                      tabIndex={-1}
                      aria-hidden="true"
                    />

                    <span className="venue-tile-label">{tag}</span>
                    <span className="venue-tile-sub">
                      {checked ? "Selected" : "Select"}
                    </span>
                  </button>
                );
              })}
            </div>
          </FormField>

          <FormField
            label="Featured priority"
            error={errors.featuredPriority}
            hint="Higher numbers appear first in Featured Venues. Use 0 for no manual priority; unprioritized venues stay newest-first."
          >
            <FormInput
              type="number"
              min="0"
              step="1"
              value={form.featuredPriority ?? 0}
              onChange={(event) => {
                set("featuredPriority", event.target.value);
                clearError("featuredPriority");
              }}
              disabled={disableTags}
            />
          </FormField>

          <FormField label="Cancellation Policy" className="venue-span-2">
            <FormTextarea
              rows={3}
              value={form.policies?.cancellationPolicy || ""}
              onChange={(event) =>
                setPolicy("cancellationPolicy", event.target.value)
              }
              placeholder="Free cancellation within 24 hours."
            />
          </FormField>

          <FormField label="Rules / Terms" className="venue-span-2">
            <FormTextarea
              rows={3}
              value={form.policies?.rulesTerms || ""}
              onChange={(event) => setPolicy("rulesTerms", event.target.value)}
              placeholder="Clean shoes only…"
            />
          </FormField>

          <FormField label="Open Time">
            <FormInput
              type="time"
              value={form.policies?.openTime || "06:00"}
              onChange={(event) => setPolicy("openTime", event.target.value)}
            />
          </FormField>

          <FormField label="Close Time">
            <FormInput
              type="time"
              value={form.policies?.closeTime || "22:00"}
              onChange={(event) => setPolicy("closeTime", event.target.value)}
            />
          </FormField>

          <FormField label="Base Price" error={errors.basePrice}>
            <FormInput
              type="number"
              min="0"
              step="0.01"
              value={form.pricing?.basePrice ?? ""}
              onChange={(event) => {
                setPricing("basePrice", event.target.value);
                clearError("basePrice");
              }}
              placeholder="1200"
            />
          </FormField>

          <FormField label="Active">
            <FormCheckbox
              checked={!!form.active}
              onChange={(event) => set("active", event.target.checked)}
              label="Active"
            />
          </FormField>
        </div>
      </div>
    </FormModal>
  );
};

export default VenueForm;